import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { FileText, Upload, Sparkles, CheckCircle, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function LandingPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [resumeText, setResumeText] = useState("");
  const [candidateName, setCandidateName] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);

  const handleFileUpload = async (file) => {
    if (!file) return;

    const allowedTypes = ['application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowedTypes.includes(file.type)) {
      toast.error("Please upload a PDF or DOCX file");
      return;
    }

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (candidateName) {
        formData.append('candidate_name', candidateName);
      }

      const response = await axios.post(`${API}/resume/upload-file`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Resume evaluated successfully!");
      navigate(`/results/${response.data.evaluation_id}`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to evaluate resume");
    } finally {
      setLoading(false);
    }
  };

  const handleTextEvaluation = async () => {
    if (!resumeText.trim() || resumeText.trim().length < 50) {
      toast.error("Please enter at least 50 characters of resume text");
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/resume/evaluate-text`, {
        resume_text: resumeText,
        candidate_name: candidateName || null
      });

      toast.success("Resume evaluated successfully!");
      navigate(`/results/${response.data.evaluation_id}`);
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.detail || "Failed to evaluate resume");
    } finally {
      setLoading(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFileUpload(e.dataTransfer.files[0]);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <Header />
      
      {/* Hero Section */}
      <div className="px-6 md:px-12 lg:px-24 py-16">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="text-center mb-20 animate-fadeIn">
            <div className="inline-flex items-center gap-2 bg-[#F8FAFC] border border-slate-200 rounded-full px-6 py-3 mb-8">
              <Sparkles className="w-4 h-4 text-[#2563EB]" />
              <span className="text-sm font-medium text-[#0F172A] tracking-wide uppercase">ElevateX AI-Powered Resume Analysis</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#0F172A] tracking-tight mb-6">
              Get Your Resume Scored
              <br />
              <span className="text-[#2563EB]">Land Your Dream Job</span>
            </h1>
            <p className="text-base md:text-lg text-[#64748B] max-w-2xl mx-auto leading-relaxed">
              Upload your resume and receive an instant, comprehensive evaluation with actionable feedback from our AI career coach. Powered by ElevateX Solutions to help you succeed.
            </p>
          </div>

          {/* Main Upload Card */}
          <Card className="border-slate-200 shadow-lg hover:shadow-xl transition-all max-w-4xl mx-auto animate-fadeIn-delay-1">
            <CardContent className="p-8 md:p-12">
              <Tabs defaultValue="upload" className="w-full">
                <TabsList className="grid w-full grid-cols-2 mb-8" data-testid="evaluation-tabs">
                  <TabsTrigger value="upload" data-testid="upload-tab" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
                    <Upload className="w-4 h-4 mr-2" />
                    Upload File
                  </TabsTrigger>
                  <TabsTrigger value="paste" data-testid="paste-tab" className="data-[state=active]:bg-[#2563EB] data-[state=active]:text-white">
                    <FileText className="w-4 h-4 mr-2" />
                    Paste Text
                  </TabsTrigger>
                </TabsList>

                {/* Name Input */}
                <div className="mb-8">
                  <label className="block text-sm font-medium text-[#0F172A] mb-2">Your Name (Optional)</label>
                  <input
                    type="text"
                    placeholder="John Doe"
                    value={candidateName}
                    onChange={(e) => setCandidateName(e.target.value)}
                    className="w-full h-12 px-4 bg-[#F1F5F9] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent"
                    data-testid="candidate-name-input"
                  />
                </div>

                <TabsContent value="upload">
                  {/* Drag and Drop Upload */}
                  <div
                    className={`border-2 border-dashed rounded-lg p-24 text-center transition-all hover:border-[#2563EB] hover:bg-[#F8FAFC] ${
                      dragActive ? 'border-[#2563EB] bg-[#F8FAFC]' : 'border-slate-300'
                    }`}
                    onDragEnter={handleDrag}
                    onDragLeave={handleDrag}
                    onDragOver={handleDrag}
                    onDrop={handleDrop}
                    onClick={() => fileInputRef.current?.click()}
                    data-testid="file-upload-zone"
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept=".pdf,.docx"
                      onChange={(e) => handleFileUpload(e.target.files[0])}
                      className="hidden"
                      data-testid="file-input"
                    />
                    <Upload className="w-16 h-16 text-[#64748B] mx-auto mb-6" />
                    <h3 className="text-2xl font-semibold text-[#0F172A] mb-3">Drop your resume here</h3>
                    <p className="text-[#64748B] mb-6">or click to browse</p>
                    <div className="flex items-center justify-center gap-4 text-sm text-[#64748B]">
                      <span>Supports: PDF, DOCX</span>
                    </div>
                  </div>
                </TabsContent>

                <TabsContent value="paste">
                  <div>
                    <textarea
                      placeholder="Paste your resume text here..."
                      value={resumeText}
                      onChange={(e) => setResumeText(e.target.value)}
                      className="w-full min-h-[400px] p-6 bg-[#F1F5F9] border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#2563EB] focus:border-transparent resize-none"
                      data-testid="resume-text-input"
                    />
                    <Button
                      onClick={handleTextEvaluation}
                      disabled={loading}
                      className="w-full mt-6 h-14 bg-[#2563EB] hover:bg-[#1e40af] text-white text-lg font-semibold rounded-full transition-all hover:scale-105"
                      data-testid="evaluate-text-button"
                    >
                      {loading ? (
                        <span className="flex items-center gap-2">
                          <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                          </svg>
                          Analyzing...
                        </span>
                      ) : (
                        <span className="flex items-center gap-2">
                          <Sparkles className="w-5 h-5" />
                          Evaluate Resume
                        </span>
                      )}
                    </Button>
                  </div>
                </TabsContent>
              </Tabs>
            </CardContent>
          </Card>

          {/* Features Grid */}
          <div className="grid md:grid-cols-3 gap-8 mt-20 animate-fadeIn-delay-2">
            <Card className="border-slate-200 hover-lift" data-testid="feature-comprehensive">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-[#2563EB] rounded-lg flex items-center justify-center mb-6">
                  <CheckCircle className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Comprehensive Analysis</h3>
                <p className="text-[#64748B] leading-relaxed">
                  Get detailed scores across 5 key categories including skills, experience, formatting, and ATS compatibility.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover-lift" data-testid="feature-actionable">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-[#10B981] rounded-lg flex items-center justify-center mb-6">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Actionable Feedback</h3>
                <p className="text-[#64748B] leading-relaxed">
                  Receive specific recommendations to improve your resume and increase your chances of landing interviews.
                </p>
              </CardContent>
            </Card>

            <Card className="border-slate-200 hover-lift" data-testid="feature-instant">
              <CardContent className="p-8">
                <div className="w-12 h-12 bg-[#F59E0B] rounded-lg flex items-center justify-center mb-6">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-xl font-semibold text-[#0F172A] mb-3">Instant Results</h3>
                <p className="text-[#64748B] leading-relaxed">
                  Powered by AI, get your resume evaluation in seconds. No waiting, no hassle.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {loading && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <Card className="p-8 max-w-md">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-[#2563EB] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">Analyzing Your Resume</h3>
              <p className="text-[#64748B]">Our AI is evaluating your resume...</p>
            </div>
          </Card>
        </div>
      )}
      
      {/* Footer */}
      <Footer />
    </div>
  );
}